import { useMap } from 'hooks';
import { useState, useRef } from 'preact/hooks';
import { useTranslation } from 'react-i18next';
import type {
  AttachmentRef,
  ReturnLineItem,
  ReturnReason,
  ReturnRequestError,
  ReturnSubmissionFormData,
} from '../interfaces/ReturnRequest.interface';

const useReturnData = () => {
  const { t } = useTranslation();
  const [
    selectedLineItems,
    { set: setSelectedLineItem, delete: deleteSelectedLineItem },
  ] = useMap<string | number, ReturnLineItem>();

  const attachmentRef = useRef<AttachmentRef>(null);

  const [comment, setComment] = useState('');

  // When tick/untick line item checkboxes
  const onCheckLineItems = (returnItem: ReturnLineItem, key: string) => {
    setSelectedLineItem(key, returnItem);
  };

  const onUncheckLineItems = (key: string) => {
    deleteSelectedLineItem(key);
  };

  // When select line item quantity
  const onSelectItemQuantity = (quantity: number, key: string) => {
    setSelectedLineItem(key, {
      ...selectedLineItems.get(key),
      quantity,
    });
  };

  // When select line item reason for return
  const onSelectReasonForReturn = (reason: ReturnReason, key: string) => {
    if (reason.value !== 'RETURN_REASON_OTHER') {
      setSelectedLineItem(key, {
        ...selectedLineItems.get(key),
        reason,
        customReason: '',
      });
    } else {
      setSelectedLineItem(key, {
        ...selectedLineItems.get(key),
        reason,
      });
    }
  };

  // When change custom reason for return
  const onChangeCustomReasonForReturn = (value: string, key: string) => {
    setSelectedLineItem(key, {
      ...selectedLineItems.get(key),
      customReason: value,
    });
  };

  const onUpdateComment = (comment: string) => {
    setComment(comment);
  };

  // --- VALIDATIONS ---

  const onValidateReturnData = (): ReturnRequestError => {
    const errors: ReturnRequestError = {};
    const data = Array.from(selectedLineItems.values());

    if (data.length < 1) {
      errors.error = t('RETURN_FORM_ERROR_MISSING_ITEM');
      return errors;
    }

    const isLineItemsInvalid = data.some(
      (item) =>
        !item.reason?.value ||
        (item.reason?.value === 'RETURN_REASON_OTHER' &&
          (item.customReason.length < 3 ||
            !/^(?=.*[a-zA-Z0-9])[\s\S]+$/.test(item.customReason))),
    );

    if (isLineItemsInvalid) {
      errors.lineItems = {};
      errors.error = t('RETURN_FORM_ERROR_MISSING_REASON_FOR_RETURN');
      data.forEach((item) => {
        errors.lineItems[item.item.guid] = {};

        if (!item.reason) {
          errors.lineItems[item.item.guid].reason = t('FORM_ERROR_EMPTY_FIELD');
        }

        if (
          item.reason?.value === 'RETURN_REASON_OTHER' &&
          (item.customReason.length < 3 ||
            !/^(?=.*[a-zA-Z0-9])[\s\S]+$/.test(item.customReason))
        ) {
          errors.lineItems[item.item.guid].customReason = t(
            'FORM_ERROR_INVALID_OTHER_REASONS',
          );
        }
      });

      return errors;
    }

    if (attachmentRef.current?.isLoading()) {
      errors.error = t('FORM_ERROR_ATTACHMENTS_UPLOADING');
      return errors;
    }
    if (attachmentRef.current?.hasError()) {
      errors.error = t('FORM_ERROR_ATTACHMENTS_ISSUE');
      return errors;
    }

    return errors;
  };
  // --- END VALIDATIONS ---

  const trimOtherReasons = () => {
    selectedLineItems.forEach((value, key) => {
      const updatedValue = { ...value };
      updatedValue.customReason = updatedValue.customReason.trim();
      selectedLineItems.set(key, updatedValue);
    });
  };

  const returnRequestFormData: ReturnSubmissionFormData = {
    selectedLineItems,
    comment,
    attachments: attachmentRef.current
      ?.getAttachments()
      ?.map((item) => ({ name: item.file.name, url: item.fileUrl })),
  };

  return {
    // update components
    onCheckLineItems,
    onUncheckLineItems,
    onSelectItemQuantity,
    onSelectReasonForReturn,
    onChangeCustomReasonForReturn,
    onUpdateComment,

    attachmentRef,

    // validate components
    onValidateReturnData,

    returnRequestFormData,
    trimOtherReasons,
  };
};

export { useReturnData };
